import React, { useState, useEffect } from "react";
import { Link } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';

// Auto-cycling Image Slideshow Component
const ProductImageSlideshow = ({ images, alt, className }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    }, 1000); // Change image every 1 second

    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <img 
      src={images[currentImageIndex]} 
      alt={alt}
      className={className}
    />
  );
};

// Header Component
const Header = ({ cartItems }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <header className="header">
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="hover:opacity-80 transition-opacity">
            <div className="logo flex items-center space-x-2">
              <img src="/images/logo.jpg" alt="Logo" className="h-10" />
              <h1 className="text-2xl font-bold tracking-wider text-white">
                <span className="hidden md:inline">FORCING AN IDEA</span>
                <span className="inline md:hidden">FORCING</span>
              </h1>
            </div>
          </Link>

          {/* Burger Button */}
          <button
            className="md:hidden flex flex-col justify-center items-center w-10 h-10 focus:outline-none"
            aria-label="Toggle navigation menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className={`block w-6 h-0.5 bg-white mb-1 transition-all duration-200 ${menuOpen ? 'rotate-45 translate-y-1.5' : ''}`}></span>
            <span className={`block w-6 h-0.5 bg-white mb-1 transition-all duration-200 ${menuOpen ? 'opacity-0' : ''}`}></span>
            <span className={`block w-6 h-0.5 bg-white transition-all duration-200 ${menuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
          </button>

          {/* Navigation */}
          <nav
            className={`$${' '}md:flex items-center space-x-8 ${menuOpen ? 'block absolute top-20 right-4 bg-gray-900 rounded shadow-lg p-4 z-50' : 'hidden'} md:static md:bg-transparent md:shadow-none md:p-0 md:block`}
          >
            <Link
              to="/"
              className="nav-link text-white hover:text-gray-300 transition-colors duration-200 underline block md:inline"
              onClick={() => setMenuOpen(false)}
            >
              Archive
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
};

// Product Grid Component
const ProductGrid = ({ products, onProductClick }) => {
  return (
    <main className="main-content">
      <div className="container mx-auto px-4 py-8">
        <div className="product-grid">
          {products.map((product) => (
            <div 
              key={product.id} 
              className="product-card aspect-square w-full max-w-xs mx-auto sm:aspect-auto sm:max-w-full flex flex-col items-center justify-between"
              onClick={() => onProductClick(product)}
            >
              <div className="product-image-container aspect-square w-full h-auto flex items-center justify-center">
                <ProductImageSlideshow 
                  images={product.images} 
                  alt={product.title}
                  className="product-image object-cover w-full h-full rounded"
                />
              </div>
              <div className="product-info w-full text-center mt-2">
                <h3 className="product-title">{product.title}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
};

// Product Modal Component
const ProductModal = ({ product, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState(null);

  if (!product) return null;

  const handleSlideChange = (swiper) => {
    setCurrentSlide(swiper.realIndex);
  };

  const handleIndicatorClick = (index) => {
    if (swiperInstance) {
      swiperInstance.slideToLoop(index);
    }
  };

  const handlePurchaseClick = () => {
    const phoneNumber = '+6285117133562';
    const message = `I would like to buy ${product.title}`;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button 
          className="modal-close"
          onClick={onClose}
        >
          ×
        </button>
        
        <div className="modal-body">
          <div className="modal-image-container">
            <Swiper
              spaceBetween={0}
              slidesPerView={1}
              loop={product.images.length > 1}
              className="modal-image-swiper"
              onSwiper={setSwiperInstance}
              onSlideChange={handleSlideChange}
            >
              {product.images.map((image, index) => (
                <SwiperSlide key={index}>
                  <img
                    src={image}
                    alt={`${product.title} - Image ${index + 1}`}
                    className="modal-image"
                  />
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Image Indicators */}
            {product.images.length > 1 && (
              <div className="image-indicators">
                {product.images.map((_, index) => (
                  <div
                    key={index}
                    className={`indicator ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => handleIndicatorClick(index)}
                    style={{ cursor: 'pointer' }}
                  />
                ))}
              </div>
            )}
          </div>
          
          <div className="modal-details">
            <h2 className="modal-title">{product.title}</h2>
            <p className="modal-description">{product.description}</p>
            
            <div className="modal-actions">
              <button 
                className="purchase-btn"
                onClick={handlePurchaseClick}
              >
                Purchase
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


export { Header, ProductGrid, ProductModal };